import { memoize } from 'proxy-memoize';
import {
  createContext,
  type DependencyList,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { proxy, subscribe, type useSnapshot } from 'valtio';
import { useProxy } from './useProxy';
export { ref, subscribe } from 'valtio';

/** * Memoization decorator for Valtio stores.
 * This decorator caches the result of a getter method
 * to avoid unnecessary recomputation during rendering.
 * It uses the `memoize` function from 'proxy-memoize' to achieve this.
 * While this is typically not needed for Valtio stores,
 * it can be useful for instances where we want to pass values into useMemo or useCallback
 * without triggering re-renders.
 */
export function memoized(_: any, __: string, descriptor: PropertyDescriptor) {
  const originalMethod = descriptor.get!;
  const memo = memoize((self) => originalMethod.call(self));
  descriptor.get = function () {
    return memo(this);
  };
}

export abstract class Store {
  initialize?(): void;
}

export function createStore<T extends Store, ARGS = undefined>(
  StoreClass: new (args: ARGS) => T,
  options?: NonNullable<Parameters<typeof useSnapshot>[1]>,
) {
  const StoreContext = createContext<T>(undefined as any);

  type ProviderProps<ARGS> = ARGS extends undefined
    ? { children: ReactNode }
    : { children: ReactNode } & ARGS;

  const Provider: FC<ProviderProps<ARGS>> = (props) => {
    const { current } = useRef(proxy(new StoreClass(props as ARGS)));
    current.initialize?.();
    return <StoreContext.Provider value={current}>{props.children}</StoreContext.Provider>;
  };

  const useStore = (): T => {
    const state = useContext(StoreContext);
    if (state === undefined) {
      throw new Error(`Store not found: ${StoreClass.name}`);
    }
    return useProxy(state, options);
  };

  return { Provider, useStore };
}

function isConstructor(fn: any): boolean {
  return typeof fn === 'function' && !!fn.prototype && !!fn.prototype.constructor;
}
export function useLocalStore<T extends Store>(
  StoreClass: (new () => T) | (() => T),
  options?: NonNullable<Parameters<typeof useSnapshot>[1]>,
  deps: DependencyList = [],
): T {
  const current = useMemo(() => {
    if (isConstructor(StoreClass)) {
      return proxy(new (StoreClass as new () => T)());
    }
    return proxy((StoreClass as () => T)());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Initialize only once
  useLayoutEffect(() => {
    current.initialize?.();
  }, [current]);

  return useProxy(current, options);
}

/**
 * Re-render this component when a valtio proxy (or anything nested under it) changes.
 *
 * Use when the proxy is first read after the owning page's snapshot-tracking pass
 * — e.g. UI that mounts only once a collapsible is expanded. Those deep reads
 * (`step.params.model`, ...) never land in the parent's `useProxy` tracking, so
 * mutating them would not re-render anything.
 *
 * Pass a getter so the target is read after render. During render, `useProxy`
 * surfaces snapshots; `subscribe` must attach to the live proxy.
 *
 *   useRerenderOnChange(() => store.filterChainConfig);
 */
export function useRerenderOnChange<T extends object>(getTarget: () => T | null | undefined): void {
  const [, forceRender] = useReducer((n) => n + 1, 0);
  const getTargetRef = useRef(getTarget);
  getTargetRef.current = getTarget;
  const target = getTarget();

  useEffect(() => {
    const live = getTargetRef.current();
    if (!live) return;

    return subscribe(live, forceRender, true);
  }, [target]);
}
